import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { Stack } from '@mui/system'
import dayjs, { Dayjs } from 'dayjs'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { SerializableAppointmentEntry } from '../../models/SerializableSdkTypes/SerializableAppointmentEntry'
import { NotificationContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useHandleAppointmentScheduleRequestResponseMutation } from '../../redux/api'
import {
  CustomerEntry,
  OneTimeReminderInfoViaEnum,
  VehicleEntry
} from '../../sdk'
import { defaultTimeZoneDateToUtcDate, describeVehicle } from '../../utils'
import { describeCustomer } from '../../utils/describeCustomer/describeCustomer'
import { localDateTimeArrayToDate } from '../../utils/localDateTimeArraytoDate/localDateTimeArrayToDate'
import { ReminderContent } from '../Reminder/ReminderContent'
import { SelectReminderVia } from '../Reminder/SelectReminderVia'
import { DatePickerWithAlert } from '../SharedUI/DatePickerWithAlert'

function formatDateToCustomerDateTime(dateTime: Date): string {
  return dayjs(dateTime).format('dddd, YYYY-MM-DD [at] hh:mm A')
}

interface Props {
  open: boolean
  handleCancel: () => void
  handleClose?: () => void
  appointment: SerializableAppointmentEntry
  customer: CustomerEntry
  vehicle: VehicleEntry
  defaultNewDateTime?: Dayjs
}

export const ProposeNewDayTimeDialog: FC<Props> = ({
  open,
  handleCancel,
  handleClose,
  appointment,
  customer,
  vehicle,
  defaultNewDateTime,
  ...props
}) => {
  const { shopProfile } = useContext(AppContext)
  const { setNotice } = useContext(NotificationContext)

  const { accessToken, shopId } = useAccessTokenAndShopId()

  const [handleAppointmentScheduleRequestResponse] =
    useHandleAppointmentScheduleRequestResponseMutation()

  const [vias, setVias] = useState<OneTimeReminderInfoViaEnum[]>([
    OneTimeReminderInfoViaEnum.Email,
    OneTimeReminderInfoViaEnum.Sms
  ])
  const [newDateTime, setNewDateTime] = useState<Dayjs | null>(
    defaultNewDateTime || dayjs()
  )

  const defaultMessage = useMemo(() => {
    return (
      'Hi ' +
      describeCustomer(customer) +
      ',\n\nUnfortunately, due to technical issues, we are unable to perform the inspection on your ' +
      describeVehicle(vehicle.info) +
      ' as scheduled for ' +
      formatDateToCustomerDateTime(
        localDateTimeArrayToDate(appointment.info?.startDateTime)
      ) +
      '.\n\nCould we possibly reschedule it for ' +
      formatDateToCustomerDateTime(newDateTime?.toDate() || dayjs().toDate()) +
      '?\n\nWe sincerely apologize for any inconvenience.\n\n' +
      (shopProfile?.info?.name || '')
    )
  }, [
    appointment.info?.startDateTime,
    customer,
    newDateTime,
    shopProfile?.info?.name,
    vehicle.info
  ])

  const [title, setTitle] = useState<string>(
    (shopProfile?.info?.name || '') + ': Reschedule Appointment'
  )
  const [message, setMessage] = useState<string>(defaultMessage)

  const handleSetDefaultMessage = useCallback(() => {
    setMessage(defaultMessage)
  }, [defaultMessage])

  // validate if message contains new date time
  const [showAlert, setShowAlert] = useState<boolean>(false)

  const checkMessageContainsNewDateTime = useCallback(() => {
    if (newDateTime === null) {
      setShowAlert(true)
      return
    }

    const formattedNewDateTime = formatDateToCustomerDateTime(
      newDateTime.toDate()
    )

    if (!message.includes(formattedNewDateTime)) {
      setShowAlert(true)
    } else {
      setShowAlert(false)
    }
  }, [message, newDateTime])

  useEffect(() => {
    checkMessageContainsNewDateTime()
  }, [message, newDateTime, checkMessageContainsNewDateTime])

  const handleSend = useCallback(async () => {
    if (newDateTime === null) {
      return
    }

    await handleAppointmentScheduleRequestResponse({
      authorization: accessToken,
      appointmentScheduleResponseRequest: {
        appointmentId: appointment.id,
        shopId,
        isRequestAccepted: false,
        sendEmail: vias.includes(OneTimeReminderInfoViaEnum.Email),
        sendSms: vias.includes(OneTimeReminderInfoViaEnum.Sms),
        newDateTime: defaultTimeZoneDateToUtcDate(newDateTime.toDate()),
        title,
        message
      }
    })

    setNotice('The appointment has been updated')

    if (handleClose) {
      handleClose()
    } else {
      handleCancel()
    }
  }, [
    accessToken,
    appointment.id,
    handleAppointmentScheduleRequestResponse,
    handleCancel,
    handleClose,
    message,
    newDateTime,
    setNotice,
    shopId,
    title,
    vias
  ])

  return (
    <Dialog
      {...props}
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="md">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        Propose New Day/Time
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Box>
            <Typography variant="subtitle1">Select Sending Method</Typography>
            <SelectReminderVia
              vias={vias}
              setVias={(newValue) => setVias(newValue)}
              customer={customer}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" pb={2}>
              Select New Timing
            </Typography>
            <DatePickerWithAlert
              label={'New Day/Time'}
              alertCondition={!newDateTime}
              alertText={'No timing selected'}
              value={newDateTime}
              onChange={(newDate) => {
                setNewDateTime(newDate)
              }}
              canClear={false}
              withTime={true}
              sx={{ width: '250px' }}
            />
          </Box>

          <Stack
            direction="row"
            spacing={2}
            display="flex"
            justifyContent="flex-end">
            {showAlert && (
              <Alert severity="warning">
                Your message does not seem to include the new proposed date and
                time. Click &quot;Use Default Message&quot; button to update
                automatically.
              </Alert>
            )}

            <Button
              onClick={() => handleSetDefaultMessage()}
              variant="outlined"
              size="small"
              sx={{ width: '200px' }}>
              Use default message
            </Button>
          </Stack>

          <ReminderContent
            vias={vias}
            title={title}
            setTitle={(newValue) => setTitle(newValue)}
            message={message}
            setMessage={(newValue) => setMessage(newValue)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={newDateTime === null}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}
